import { useCallback, useEffect, useRef, useState } from 'react'
import { chordOf } from '../core/keymap.ts'
import { HEADINGS, clamped, composing, moved, overview, search, type Hit, type PaletteEntry, type Screen, type Section } from '../core/palette.ts'
import { useChrome, useChromeState, usePointerMoved } from './hooks.ts'

/// The command palette (design-system plan, decision 15). Empty, it lists recent commands and the
/// screen's own; typed, it lists what the label names, then what an alias names, then the close
/// matches under their own heading. It is a modal dialog around a combobox: focus stays in the
/// input, which points at the selected option, and a status line announces how many there are.
/// Enter runs the selected one unless an input method is composing, Escape closes, and focus goes
/// back to where it was. Its input stays in the page while the palette is closed, so the chord
/// can give it the focus at once (docs/design/ux-review.md, unit 4). `heading` names the empty
/// palette's group for what is open; the aligner's page names its session.
export function Palette({ heading = HEADINGS.project }: { heading?: string }) {
  const shell = useChrome()
  const state = useChromeState()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const moved_ = usePointerMoved()
  const list = useRef<HTMLDivElement>(null)
  const open = state.palette
  const input = useCallback((el: HTMLInputElement | null) => {
    const typed = shell.paletteMounted(el)
    if (typed) setQuery(typed)
  }, [shell])
  useEffect(() => {
    if (open) return
    setQuery('')
    setSelected(0)
    shell.paletteClosed()
  }, [shell, open])

  const sections = open ? sectionsFor(shell.paletteEntries(), query.trim() === '' ? null : query, shell.paletteScreen, shell.recentCommands(), heading) : []
  const hits = sections.flatMap(s => s.hits)
  const at = clamped(selected, hits.length)
  const optionId = (id: string) => `palette-option-${id}`
  useEffect(() => {
    list.current?.querySelector('[aria-selected=true]')?.scrollIntoView({ block: 'nearest' })
  }, [at, query])

  const close = () => shell.set({ palette: false })
  const run = (id: string) => {
    close()
    requestAnimationFrame(() => shell.command(id, 'palette'))
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    const chord = chordOf(e.nativeEvent, shell.platform)
    if (e.key === 'Escape' || (chord !== null && chord === shell.registry.chord('palette.open', shell.platform))) {
      e.preventDefault()
      close()
    } else if (e.key === 'Tab') {
      // The editor behind is not reachable: the input is the one stop.
      e.preventDefault()
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected(moved(at, e.key === 'ArrowDown' ? 1 : -1, hits.length))
    } else if (e.key === 'Enter') {
      if (composing(e.nativeEvent)) return
      e.preventDefault()
      const hit = hits[at]
      if (hit) run(hit.entry.id)
    }
  }

  let index = -1
  return (
    <div className={open ? 'palette' : 'palette closed'} aria-hidden={!open} onMouseDown={e => { if (open && e.target === e.currentTarget) close() }}>
      <div className="pbox" role="dialog" aria-modal="true" aria-label="Command palette" onKeyDown={onKeyDown}>
        <input
          ref={input}
          value={query}
          tabIndex={open ? 0 : -1}
          role="combobox"
          aria-label="Command"
          aria-expanded={open}
          aria-controls={open ? 'palette-list' : undefined}
          aria-activedescendant={open && at >= 0 ? optionId(hits[at]!.entry.id) : undefined}
          aria-autocomplete="list"
          placeholder="Type a command"
          autoComplete="off"
          spellCheck={false}
          onChange={e => { setQuery(e.target.value); setSelected(0) }}
        />
        {open ? (
          <>
            <div id="palette-list" ref={list} role="listbox" aria-label="Commands" className="plist">
              {sections.map((section, s) => (
                <div key={section.heading || s} role="group" aria-labelledby={section.heading ? `palette-heading-${s}` : undefined}>
                  {section.heading ? <div id={`palette-heading-${s}`} className="phead">{section.heading}</div> : null}
                  {section.hits.map(hit => {
                    index += 1
                    const mine = index
                    const entry = hit.entry
                    const chord = shell.shownChord(entry.id)
                    return (
                      <div key={entry.id} id={optionId(entry.id)} role="option" aria-selected={mine === at} data-command={entry.id} className={mine === at ? 'popt sel' : 'popt'} onMouseMove={e => { if (moved_(e)) setSelected(mine) }} onMouseDown={e => { e.preventDefault(); run(entry.id) }}>
                        <span className="ptext">
                          <span className="plabel">{marked(entry.label, hit.spans)}</span>
                          {entry.note ? <span className="pnote">{entry.note}</span> : null}
                        </span>
                        {entry.where ? <span className="pmenu">{entry.where}</span> : null}
                        {chord ? <kbd>{chord}</kbd> : null}
                      </div>
                    )
                  })}
                </div>
              ))}
              {hits.length === 0 ? <div className="pnone">No command matches</div> : null}
            </div>
            <div className="sr-only" role="status">{announcement(sections)}</div>
            <div className="phint"><kbd>Up</kbd><kbd>Down</kbd> choose · <kbd>Enter</kbd> run · <kbd>Esc</kbd> close</div>
          </>
        ) : null}
      </div>
    </div>
  )
}

/// What the list holds: the empty palette's groups, or a typed query's matches with the close
/// ones under their own heading.
function sectionsFor(entries: PaletteEntry[], query: string | null, screen: Screen, recent: string[], heading: string): Section[] {
  if (query === null) return overview(entries, screen, recent, heading)
  const found = search(entries, query, recent)
  return [
    ...(found.matches.length > 0 ? [{ heading: '', hits: found.matches }] : []),
    ...(found.close.length > 0 ? [{ heading: HEADINGS.close, hits: found.close }] : []),
  ]
}

/// The label with the matched stretches in weight 600.
function marked(label: string, spans: Hit['spans']) {
  const parts: React.ReactNode[] = []
  let from = 0
  for (const [start, end] of spans) {
    if (start < from) continue
    if (start > from) parts.push(label.slice(from, start))
    parts.push(<b key={start}>{label.slice(start, end)}</b>)
    from = end
  }
  if (from < label.length) parts.push(label.slice(from))
  return parts
}

/// The result count a screen reader hears when the list changes.
function announcement(sections: Section[]): string {
  const close = sections.find(s => s.heading === HEADINGS.close)?.hits.length ?? 0
  const total = sections.reduce((n, s) => n + s.hits.length, 0)
  if (total === 0) return 'No command matches'
  const count = `${total} ${total === 1 ? 'command' : 'commands'}`
  return close > 0 ? `${count}, ${close} of them close matches` : count
}
